import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useFeatureFlags } from './useFeatureFlags.jsx';
import unifiedApi from '../services/unifiedApi';
import { toast } from 'sonner';
import logger from '@/utils/logger';
import { getToken } from '@/utils/storage';

const _parseJwt = (token) => {
  try {
    const binary = atob(token.split('.')[1]);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return JSON.parse(new TextDecoder('utf-8').decode(bytes));
  } catch {
    return null;
  }
};

export const useParents = () => {
  const queryClient = useQueryClient();

  const token = getToken();
  const isImpersonating = !!_parseJwt(token)?.impersonated_by;
  const [searchParams, setSearchParams] = useSearchParams();
  const [showModal, setShowModal] = useState(false);
  const [editingParent, setEditingParent] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [parentToDelete, setParentToDelete] = useState(null);
  const [filters, setFilters] = useState({
    search: searchParams.get('q') || '',
    status: searchParams.get('status') || 'active',
  });

  const { hasFeature } = useFeatureFlags();

  const {
    data,
    isPending: loading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: ['parents'],
    queryFn: async () => {
      const [parentsResponse, statsResponse] = await Promise.all([
        unifiedApi.parents.getAllUnlimited(),
        hasFeature('metrics') ? unifiedApi.parents.getStats() : Promise.resolve(null),
      ]);
      if (!parentsResponse.success) throw new Error(parentsResponse.error || 'Erreur lors du chargement des parents');
      return {
        parents: parentsResponse.data || [],
        stats: statsResponse?.data || null,
      };
    },
  });

  const error = queryError?.message ?? null;
  const parents = data?.parents || [];
  const stats = data?.stats || null;
  const loadParents = refetch;

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['parents'] });

  const updateFilters = (partial) => {
    setFilters((prev) => {
      const next = { ...prev, ...partial };
      const params = new URLSearchParams(searchParams);
      if (next.search) params.set('q', next.search);
      else params.delete('q');
      if (next.status) params.set('status', next.status);
      else params.delete('status');
      setSearchParams(params, { replace: true });
      return next;
    });
  };

  const filteredParents = parents.filter((parent) => {
    const searchTerm = filters.search.toLowerCase();
    const matchesSearch =
      !filters.search ||
      parent.first_name?.toLowerCase().includes(searchTerm) ||
      parent.last_name?.toLowerCase().includes(searchTerm) ||
      parent.email?.toLowerCase().includes(searchTerm) ||
      parent.phone?.includes(searchTerm);
    const matchesStatus = !filters.status || parent.status === filters.status;
    return matchesSearch && matchesStatus;
  });

  const handleEdit = (parent) => {
    setEditingParent(parent);
    setShowModal(true);
  };

  const handleDeleteParent = (parent) => {
    setParentToDelete(parent);
    setDeleteModalOpen(true);
  };

  const confirmDeleteParent = async () => {
    const parent = parentToDelete;
    setDeleteModalOpen(false);
    setParentToDelete(null);
    if (!parent) return;

    const name = `${parent.first_name || ''} ${parent.last_name || ''}`.trim();
    queryClient.setQueryData(['parents'], (old) => ({
      ...old,
      parents: (old?.parents || []).filter((p) => p.id !== parent.id),
    }));

    try {
      const result = await unifiedApi.parents.delete(parent.id);
      if (!result.success) throw new Error(result.error || 'Erreur suppression');
      toast.success(`Parent ${name} supprimé`);
      invalidate();
    } catch (err) {
      logger.error('Erreur suppression parent:', err);
      invalidate();
      toast.error(`Erreur lors de la suppression de ${name}`);
    }
  };

  const handleSave = async (parentData) => {
    try {
      let result;
      if (editingParent) {
        result = await unifiedApi.parents.update(editingParent.id, parentData);
        if (result.success) {
          toast.success('Parent modifié avec succès !');
        } else {
          throw new Error(result.error || 'Erreur lors de la modification');
        }
      } else {
        result = await unifiedApi.parents.create(parentData);
        if (result.success) {
          toast.success('Parent créé avec succès !');
        } else {
          throw new Error(result.error || 'Erreur lors de la création');
        }
      }
      setShowModal(false);
      invalidate();
    } catch (err) {
      logger.error('Erreur sauvegarde parent:', err);
      toast.error(err.message || 'Erreur lors de la sauvegarde du parent');
      throw err;
    }
  };

  return {
    parents,
    filteredParents,
    loading,
    error,
    stats,
    showModal,
    editingParent,
    deleteModalOpen,
    parentToDelete,
    filters,
    updateFilters,
    setShowModal,
    setDeleteModalOpen,
    loadParents,
    handleEdit,
    handleDeleteParent,
    confirmDeleteParent,
    handleSave,
    isImpersonating,
  };
};

export default useParents;
